import gql from 'graphql-tag';
import { useApolloClient } from '@keap-web/apollo-lib';

export const useInstallQueries = () => {
    const apolloClient = useApolloClient();

    const getRequestInfo = (installRequestId) => {
        return apolloClient.query({
            query: gql`
                query installRequest($installRequestId: ID!) {
                    installRequest(installRequestId: $installRequestId) {
                        title
                        description
                        linkId
                        resourceType
                        tenantId
                        status
                        dependencies {
                            sharedResourceId
                            title
                            description
                            resourceType
                            resourceSubtype
                            resourceId
                        }
                        company {
                            name
                            logoUrl
                        }
                        topLevel
                        sharedResourceId
                        updateTime
                        createTime
                        failureReasons
                    }
                }
            `,
            variables: {
                installRequestId,
            },
            fetchPolicy: 'no-cache',
        });
    };

    const getBatchRequestInfo = ({ batchInstallRequestId, pageSize, statuses }) => {
        return apolloClient.query({
            query: gql`
                query batchInstallRequest($batchInstallRequestId: ID!, $statuses: String, $pageSize: Int) {
                    batchInstallRequest(batchInstallRequestId: $batchInstallRequestId, statuses: $statuses, pageSize: $pageSize) {
                        title
                        description
                        resourceType
                        topLevel
                        sharedResourceId
                        updateTime
                        createTime
                        installRequests {
                            tenantId
                            status
                            requestId
                            updateTime
                        }
                    }
                }
            `,
            variables: {
                batchInstallRequestId,
                statuses: statuses ? statuses.join(',') : undefined,
                pageSize,
            },
            fetchPolicy: 'no-cache',
        });
    };

    const getLinkInfo = (linkId) => {
        return apolloClient.query({
            query: gql`
                query linkInfo($linkId: ID!) {
                    linkInfo(linkId: $linkId) {
                        dependencies {
                            sharedResourceId
                            title
                            description
                            resourceType
                            resourceSubtype
                        }
                        company {
                            name
                            logoUrl
                        }
                        sharedResourceId
                        title
                        description
                        isValid
                        linkId
                        resourceType
                        ownerType
                    }
                }
            `,
            variables: {
                linkId,
            },
            fetchPolicy: 'no-cache',
        });
    };

    const createBatchInstallRequest = ({ tenantIds, sharedResourceId }) => {
        return apolloClient.mutate({
            mutation: gql`
                mutation createBatchInstallRequest($request: CreateBatchInstallRequest!) {
                    createBatchInstallRequest(request: $request) {
                        batchRequestId
                        failedTenantIds
                    }
                }
            `,
            variables: {
                request: {
                    tenantIds,
                    sharedResourceId,
                },
            },
        });
    };

    const uninstall = (requestId) => {
        return apolloClient.mutate({
            mutation: gql`
                mutation uninstallRequest($requestId: ID!) {
                    uninstallRequest(requestId: $requestId) {
                        dependencies {
                            title
                            resourceType
                            resourceSubtype
                            resourceId
                            uninstallSuccess
                            unsupported
                        }
                    }
                }
            `,
            variables: {
                requestId,
            },
        });
    };

    const getBatchInstallsList = (request) => {
        return apolloClient.query({
            query: gql`
                    query batchInstallRequestList($request: BatchInstallListInput!) {
                        batchInstallRequestList(request: $request) {
                            batchRequests {
                                batchRequestId
                                sharedResource {
                                    sharedResourceId
                                    title
                                }
                                createTime
                                updateTime
                            }
                            totalSize
                            nextPageToken
                            resultSize
                        }
                    }
                `,
            variables: {
                request,
            },
            fetchPolicy: 'no-cache',
        });
    };

    const getBatchInstallSummary = (requestId) => {
        return apolloClient.query({
            query: gql`
                query batchInstallSummary($requestId: ID!) {
                    batchInstallSummary(requestId: $requestId) {
                        batchRequestId
                        total
                        completed
                        failed
                    }
                }
            `,
            variables: {
                requestId,
            },
            fetchPolicy: 'no-cache',
        });
    };

    return {
        getRequestInfo,
        getLinkInfo,
        uninstall,
        createBatchInstallRequest,
        getBatchRequestInfo,
        getBatchInstallsList,
        getBatchInstallSummary,
    };
};
